import React, { useState, useCallback } from "react";
import SwipeableViews from "react-swipeable-views";
import MenuOption from "./menu-items/MenuOption";
import MenuCreateDialog from "./menu-items/MenuCreateDialog";
import CategorySelectDialog from "./menu-items/CategorySelectDialog";
import CreateCategory from "./menu-items/CreateCategory";
import AddMenuItemForm from "./menu-items/AddMenuItemForm";
import SelectItems from "./menu-items/SelectItems";
import MenuOcrFlow from "./menu-items/menu-ocr-ui/MenuOcrFlow";
import EditMenuDialog from "src/pages/business/dashboard/dialogs/EditMenuDialog";
import ScheduleDialog from "src/pages/business/dashboard/dialogs/ScheduleDialog";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";

/**
 * MenuSetup Component - Handles the complete menu/catalog setup flow
 * 
 * Navigation Flow:
 * - Step 0: Main listing (MenuOption component with tabs)
 * - Step 1: Menu creation/editing dialog
 * - Step 2: Category selection dialog (for menu)
 * - Step 3: Category creation/editing
 * - Step 4: Item selection dialog (for category)
 * - Step 5: Item creation/editing form
 * - Step 6: AI Menu OCR flow (scan/upload → process → confirm)
 * 
 * Context-Aware Navigation:
 * - Individual Mode (from tabs): After creating/editing items, go back to main listing
 * - Flow Mode (from menu/category creation): After creating/editing items, go back to item selection
 */
const MenuSetup = ({ catalogType, initialTab = 0 }) => {
  const { t } = useTranslation("dashboard");
  const [activeStep, setActiveStep] = useState(0);
  const [editItem, setEditItem] = useState(null);
  const [editMenu, setEditMenu] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [editCategory, setEditCategory] = useState(null);
  const [currentTab, setCurrentTab] = useState(initialTab);

  // Publish dialog state
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishingMenu, setPublishingMenu] = useState(null);
  const [publishFormData, setPublishFormData] = useState({});

  // Schedule dialog state
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleData, setScheduleData] = useState([]);
  const [schedulingMenu, setSchedulingMenu] = useState(null);

  const selectedBusinessId = useSelector((state) => state.auth.selectedBusinessId);
  const queryClient = useQueryClient();

  const { data: locationsData } = useQuery(
    ["business-locations-setup", selectedBusinessId],
    () => axiosInstance.get(ApiEndPoints.TEAM_LINKING.get_business_locations(selectedBusinessId)).then((r) => r?.data?.data),
    { enabled: !!selectedBusinessId, staleTime: 5 * 60_000 }
  );
  const locationOptions = locationsData?.locations || [];

  const updateMenuSettingsMutation = useMutation(
    (settingsData) =>
      axiosInstance.patch(`${ApiEndPoints.MENU.add}/${publishingMenu?._id}/settings`, settingsData).then((r) => r.data),
    {
      onSuccess: () => {
        toast.success(t("menuSettingsUpdated"));
        queryClient.invalidateQueries(["menus", selectedBusinessId]);
        setPublishDialogOpen(false);
        setPublishingMenu(null);
        setPublishFormData({});
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || t("failedToUpdateMenuSettings"));
      },
    }
  );

  const handleMenuCreated = useCallback((responseData) => {
    console.log('[MenuSetup] handleMenuCreated called, responseData:', responseData);
    const menu = responseData?.data?.menu || responseData?.menu || responseData?.data || responseData;
    console.log('[MenuSetup] extracted menu:', menu, 'has _id:', !!menu?._id);
    if (!menu?._id) return;
    const locationIds = Array.isArray(menu.locationIds)
      ? menu.locationIds.map((l) => l._id || l)
      : [];
    setPublishingMenu(menu);
    setPublishFormData({
      name: menu.nameKey || menu.name || "",
      nameKey: menu.nameKey || menu.name || "",
      image: menu.image || "",
      showStockCount: menu.showStockCountToCustomer !== undefined ? menu.showStockCountToCustomer : true,
      publishMenu: menu.publishMenu !== undefined ? menu.publishMenu : false,
      selectedLocations: locationIds,
    });
    setPublishDialogOpen(true);
  }, []);

  const handleSavePublish = () => {
    updateMenuSettingsMutation.mutate({
      name: (publishFormData.nameKey || publishFormData.name || "").trim(),
      locationIds: publishFormData.selectedLocations,
      showStockCountToCustomer: publishFormData.showStockCount || false,
      publishMenu: publishFormData.publishMenu || false,
    });
  };

  const updateMenuScheduleMutation = useMutation(
    (data) =>
      axiosInstance.patch(`${ApiEndPoints.MENU.add}/${schedulingMenu?._id}/schedule`, { menuSchedule: data }).then((r) => r.data),
    {
      onSuccess: () => {
        toast.success(t("menuScheduleUpdated"));
        queryClient.invalidateQueries(["menus", selectedBusinessId]);
        setScheduleDialogOpen(false);
        setScheduleData([]);
        setSchedulingMenu(null);
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || t("failedToUpdateMenuSettings"));
      },
    }
  );

  const handleOpenSchedule = useCallback((menu) => {
    const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const existing = Array.isArray(menu?.menuSchedule) ? menu.menuSchedule : [];
    const formatted = days.map((day) => {
      const found = existing.find((s) => s.day === day);
      return found
        ? { day: found.day, openTime: found.openTime || "09:00 AM", closeTime: found.closeTime || "06:30 PM", isEnabled: found.isEnabled ?? false }
        : { day, openTime: "09:00 AM", closeTime: "06:30 PM", isEnabled: false };
    });
    setScheduleData(formatted);
    setSchedulingMenu(menu);
    setScheduleDialogOpen(true);
  }, []);

  const handleScheduleChange = useCallback((index, field, value) => {
    setScheduleData((prev) => {
      const next = [...prev];
      if (field === "openTime" || field === "closeTime") {
        let formatted = value;
        if (value && value.includes(":")) {
          const [hours, minutes] = value.split(":");
          const hour = parseInt(hours);
          const ampm = hour >= 12 ? "PM" : "AM";
          const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
          formatted = `${displayHour.toString().padStart(2, "0")}:${minutes} ${ampm}`;
        }
        next[index] = { ...next[index], [field]: formatted };
      } else {
        next[index] = { ...next[index], [field]: value };
      }
      return next;
    });
  }, []);

  const handleSaveSchedule = useCallback(() => {
    updateMenuScheduleMutation.mutate(scheduleData);
  }, [updateMenuScheduleMutation, scheduleData]);

  React.useEffect(() => {
    setCurrentTab(initialTab);
    setActiveStep(0);
    setEditItem(null);
    setEditMenu(null);
    setEditCategory(null);
    setSelectedCategories([]);
    setSelectedItems([]);
  }, [catalogType, initialTab]);

  const handleCreateOnMenuClick = () => {
    setActiveStep(6); // Move to AI OCR flow (step 6)
  };

  const handleEditMenu = (menu) => {
    setEditMenu(menu);
    setActiveStep(1); // Navigate to menu creation dialog with edit data
  };

  const handleTabChange = (newTab) => {
    setCurrentTab(newTab);
    // Reset to first step when changing tabs
    setActiveStep(0);
    setEditMenu(null);
    setEditCategory(null);
    setSelectedCategories([]);
    setSelectedItems([]);
  };

  /**
   * Handle category addition based on current context:
   * - Menu tab (0): Go to category selection for menu creation
   * - Categories tab (1): Go directly to category creation
   */
  const handleAddCategory = () => {
    if (currentTab === 0) {
      // If we're in menu tab, go to category selection for menu
      setActiveStep(2);
    } else if (currentTab === 1) {
      // If we're in categories tab, go directly to category creation
      setActiveStep(3);
    }
  };

  /**
   * Handle category editing based on current context:
   * - Menu tab (0): Go to category selection (to assign to menu)
   * - Categories tab (1): Go directly to category editing
   */
  const handleEditCategory = (category) => {
    setEditCategory(category);
    if (currentTab === 0) {
      // If we're in menu tab, go to category selection
      setActiveStep(2);
    } else if (currentTab === 1) {
      // If we're in categories tab, go directly to category creation
      setActiveStep(3);
    }
  };

  return (
    <>
    <SwipeableViews
      enableMouseEvents={false}
      index={activeStep}
      onChangeIndex={setActiveStep}
    >
      <MenuOption
        isCatalog={catalogType === "catalog"}
        catalogType={catalogType}
        initialTab={currentTab}
        createOnMenuClick={handleCreateOnMenuClick}
        onEditMenu={handleEditMenu}
        onTabChange={handleTabChange}
        onAddCategory={handleAddCategory}
        onEditCategory={handleEditCategory}
        onEditItem={(item) => {
          setEditItem(item);
          setActiveStep(5);
        }}
        onAddItem={() => {
          setEditItem(null);
          setActiveStep(5);
        }}
      />
      <MenuCreateDialog
        isCatalog={catalogType === "catalog"}
        editMenu={editMenu}
        selectedCategories={selectedCategories}
        setSelectedCategories={setSelectedCategories}
        onClose={() => {
          setActiveStep(0);
          setSelectedCategories([]);
          setEditMenu(null);
        }}
        onAddCategoryClick={() => {
          setActiveStep(2);
        }}
        onEditCategory={(category) => {
          setEditCategory(category);
          setActiveStep(3);
        }}
        onMenuCreated={!editMenu ? handleMenuCreated : undefined}
      />
      <CategorySelectDialog
        isCatalog={catalogType === "catalog"}
        selectedCategories={selectedCategories}
        handleClose={() => {
          setActiveStep(1);
          setEditCategory(null); // Reset editCategory when going back
        }}
        onCreateCategory={() => {
          setActiveStep(3);
          setEditCategory(null); // Reset editCategory for new creation
        }}
        setSelectedCategories={setSelectedCategories}
        onEditCategory={(data) => {
          console.log("Setting editCategory:", data);
          setEditCategory(data);
          setActiveStep(3);
        }}
      />
      <CreateCategory
        isCatalog={catalogType === "catalog"}
        editCategory={editCategory}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        onClose={() => {
          if (currentTab === 0) {
            setActiveStep(2); // Go back to category selection if in menu tab
          } else {
            setActiveStep(0); // Go back to main view if in categories tab
          }
          setEditCategory(null); // Reset editCategory when closing
        }}
        onMenuCreated={() => {
          if (currentTab === 0) {
            setActiveStep(1); // Go back to menu creation if in menu tab
          } else {
            setActiveStep(0); // Go back to main view if in categories tab
          }
          setEditCategory(null); // Reset editCategory after creation
        }}
        onAddItemClick={() => {
          setActiveStep(4);
        }}
      />
      <SelectItems
        isCatalog={catalogType === "catalog"}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        onCreateItem={() => {
          setEditItem(null);
          setActiveStep(5);
        }}
        handleClose={() => {
          setActiveStep(1);
        }}
        onEditItem={(data) => {
          setEditItem(data);
          setActiveStep(5);
        }}
        onAdd={() => {
          setActiveStep(3);
        }}
      />
      {editItem ? (
        <AddMenuItemForm
          isCatalog={catalogType === "catalog"}
          editItem={editItem}
          handleClose={() => {
            setEditItem(null);
            // Navigation logic based on context:
            // - If we're in items tab (tab 2): we're in individual mode, go back to main listing
            // - If we're in menu/category tabs (tab 0/1): we're in flow mode, go back to SelectItems
            if (currentTab === 2) {
              setActiveStep(0); // Go back to main listing (items tab)
            } else {
              setActiveStep(4); // Go back to SelectItems (flow mode)
            }
          }}
        />
      ) : (
        <AddMenuItemForm
          isCatalog={catalogType === "catalog"}
          handleClose={() => {
            setEditItem(null);
            // Navigation logic based on context:
            // - If we're in items tab (tab 2): we're in individual mode, go back to main listing
            // - If we're in menu/category tabs (tab 0/1): we're in flow mode, go back to SelectItems
            if (currentTab === 2) {
              setActiveStep(0); // Go back to main listing (items tab)
            } else {
              setActiveStep(4); // Go back to SelectItems (flow mode)
            }
          }}
        />
      )}
      {/* Step 6: AI Menu OCR Flow */}
      <MenuOcrFlow
        isCatalog={catalogType === "catalog"}
        onClose={() => {
          setActiveStep(0);
        }}
        onManualEntry={() => {
          setEditMenu(null);
          setActiveStep(1);
        }}
        onMenuCreated={handleMenuCreated}
      />
    </SwipeableViews>

    <EditMenuDialog
      open={publishDialogOpen}
      onClose={() => {
        setPublishDialogOpen(false);
        setPublishingMenu(null);
        setPublishFormData({});
      }}
      editingMenu={publishingMenu}
      editFormData={publishFormData}
      setEditFormData={setPublishFormData}
      locationList={locationOptions}
      tabValue={catalogType === "catalog" ? 1 : 0}
      onSave={handleSavePublish}
      onScheduleClick={handleOpenSchedule}
      isLoading={updateMenuSettingsMutation.isLoading}
    />
    <ScheduleDialog
      open={scheduleDialogOpen}
      onClose={() => {
        setScheduleDialogOpen(false);
        setScheduleData([]);
        setSchedulingMenu(null);
      }}
      scheduleData={scheduleData}
      onScheduleChange={handleScheduleChange}
      onSave={handleSaveSchedule}
      tabValue={catalogType === "catalog" ? 1 : 0}
      isLoading={updateMenuScheduleMutation.isLoading}
    />
    </>
  );
};

export default MenuSetup;
